import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import NewRequestDialog from "@/components/NewRequestDialog";
import { Inbox, MessageSquare, CheckCircle2, Clock, Plus, GraduationCap, Sprout } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function PortalHome() {
  const { user } = useAuth();
  const [newReqOpen, setNewReqOpen] = useState(false);

  const { data: requests = [] } = useQuery({
    queryKey: ["requests"],
    queryFn: async () => (await api.get("/requests")).data,
  });
  const { data: threadStats } = useQuery({
    queryKey: ["threads-stats-portal"],
    queryFn: async () => (await api.get("/threads-stats/overview")).data,
  });

  const pending = requests.filter((r) => r.status === "pending").length;
  const approved = requests.filter((r) => r.status === "approvato").length;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">
      <PageHeader
        title={`Ciao ${user?.name?.split(" ")[0] || "studente"}`}
        subtitle="Benvenuto nel portale RDM ClassRoom. Da qui gestisci richieste alla segreteria e comunicazioni."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <QuickAction
          icon={Plus}
          title="Nuova richiesta"
          desc="Permesso, certificato, prenotazione esame o colloquio"
          onClick={() => setNewReqOpen(true)}
          highlight
          testId="portal-new-request"
        />
        <Link to="/portale/richieste">
          <QuickAction icon={Inbox} title="Le mie richieste" desc={`${pending} in attesa · ${approved} approvate`} testId="portal-goto-requests"/>
        </Link>
        <Link to="/portale/messaggi">
          <QuickAction icon={MessageSquare} title="Messaggi" desc={`${threadStats?.unread || 0} non letti · ${threadStats?.total || 0} totali`} testId="portal-goto-messages"/>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-medium flex items-center gap-2"><Inbox size={14}/> Ultime richieste</div>
            <Link to="/portale/richieste" className="text-xs text-[#E2725B] hover:underline">Vedi tutte</Link>
          </div>
          {requests.length === 0 ? (
            <div className="text-sm text-stone-400 py-6 text-center">Nessuna richiesta ancora.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {requests.slice(0, 5).map((r) => (
                <li key={r.id} className="py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-stone-100 grid place-items-center shrink-0">
                    {r.status === "pending" ? <Clock size={13} className="text-amber-700"/> : <CheckCircle2 size={13} className="text-emerald-700"/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={`/portale/richieste/${r.id}`} className="text-sm font-medium hover:text-[#1C352D] truncate block">{r.subject}</Link>
                    <div className="text-[11px] text-stone-500">{r.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 border border-stone-200 rounded-xl shadow-none bg-gradient-to-br from-[#1C352D] to-[#264A3E] text-white">
          <div className="flex items-center gap-2 mb-3">
            <Sprout size={16}/>
            <div className="text-[10px] uppercase tracking-widest opacity-80">RDM ClassRoom</div>
          </div>
          <div className="text-xl font-light leading-tight">
            La tua chiamata,<br/><span className="font-semibold text-[#E2725B]">il tuo cammino.</span>
          </div>
          <div className="mt-4 text-sm opacity-90 leading-relaxed">
            Usa il portale per restare in contatto con la segreteria didattica. Ogni richiesta è tracciata e riceverai risposta via email.
          </div>
        </Card>
      </div>

      <NewRequestDialog open={newReqOpen} onOpenChange={setNewReqOpen} />
    </div>
  );
}

function QuickAction({ icon: Icon, title, desc, onClick, highlight, testId }) {
  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className={`p-5 border rounded-xl shadow-none cursor-pointer transition-all duration-150 hover:-translate-y-0.5 ${
        highlight
          ? "border-[#E2725B] bg-[#E2725B] text-white hover:bg-[#c85d47]"
          : "border-stone-200 hover:border-[#1C352D]"
      }`}
    >
      <div className={`w-10 h-10 rounded-full grid place-items-center mb-3 ${highlight ? "bg-white/20" : "bg-stone-100"}`}>
        <Icon size={18} className={highlight ? "text-white" : "text-[#1C352D]"}/>
      </div>
      <div className={`font-semibold ${highlight ? "text-white" : "text-[#1C352D]"}`}>{title}</div>
      <div className={`text-xs mt-0.5 ${highlight ? "text-white/80" : "text-stone-500"}`}>{desc}</div>
    </Card>
  );
}
